import { OverlayProvider } from '@react-aria/overlays';
import { IntersectionObserverContextProvider, LazyLoadContextProvider } from '@riac/core';
import { LinkContext, ModalScrollContextProvider, Spinner } from '@riac/design-system';
import Cookies from 'js-cookie';
import React, { Suspense, useContext, useState } from 'react';
import { Switch } from 'react-router';
import { BrowserRouter, Link, Route, StaticRouter } from 'react-router-dom';
import { CrispChat } from 'src/components/CrispChat';
import { Footer } from 'src/components/Footer/Footer';
import { Header } from 'src/components/Header/Header';
import { ScrollToTop } from 'src/components/ScrollToTop';
import { ServerSideRenderingError } from 'src/components/ServerSideRenderingError';
import { YouHaveBeenLoggedOut } from 'src/components/YouHaveBeenLoggedOut';
import { IncompleteRegistrationBanner } from 'src/components/IncompleteRegistrationBanner';
import { AccountHeaderContextProvider } from 'src/lib/AccountHeaderContext';
import { AnalyticsContextProvider } from 'src/lib/AnalyticsContext';
import { ThemeContextProvider } from 'src/lib/ThemeContext';

import { CatalogHistoryContextProvider } from 'src/lib/CatalogHistoryContext';
import ErrorBoundary from 'src/lib/ErrorBoundary';
import { GlobalBannerContextProvider } from 'src/lib/GlobalBannerContext';
import { LoginContextProvider } from 'src/lib/LoginContext';
import { PageLoader } from 'src/lib/PageLoader';
import { ScrollContextProvider } from 'src/lib/ScrollContext';
import { SocketContext } from 'src/lib/SocketContext';
import { SsrInitDataContext } from 'src/lib/SsrInitDataContext';
import { UserItemContextProvider } from 'src/lib/UserItemContext';
import { useSocketEvent } from 'src/lib/useSocketEvent';
import { WishlistActionContextProvider } from 'src/lib/WishlistActionContext';
import { LoadableErrorBoundary } from 'src/LoadableErrorBoundary';
import { AsyncAboutUsPage } from 'src/pages/AboutUsPage/AsyncAboutUsPage';
import { AsyncAdCampaignsPage } from 'src/pages/AdCampaigns/AsyncAdCampaignsPage';
import { AsyncAdCampaignsSinglePage } from 'src/pages/AdCampaigns/AsyncAdCampaignsSinglePage';
import { AsyncAuctionsPage } from 'src/pages/AuctionsPage/AsyncAuctionsPage';
import { AsyncAuctionsSinglePage } from 'src/pages/AuctionsSinglePage/AsyncAuctionsSinglePage';
import { AsyncCatalogPage } from 'src/pages/CatalogPage/AsyncCatalogPage';
import { AsyncCatalogLandingPage } from 'src/pages/CatalogPage/CatalogLandingPage/AsyncCatalogLandingPage';
import landingPages from 'src/pages/CatalogPage/landing-pages';
import {
  AsyncConsignmentGettingStartedPage,
} from 'src/pages/ConsignmentGettingStartedPage/AsyncConsignmentGettingStartedPage';
import {
  AsyncConsignmentNotableCollectorsPage,
} from 'src/pages/ConsignmentNotableCollectors/AsyncConsignmentNotableCollectorsPage';
import {
  AsyncConsignmentSingleCollectorPage,
} from 'src/pages/ConsignmentNotableCollectors/AsyncConsignmentSingleCollectorPage';
import { AsyncConsignmentPage } from 'src/pages/ConsignmentPage/AsyncConsignmentPage';
import { AsyncContactPage } from 'src/pages/ContactPage/AsyncContactPage';
import { AsyncCreateAccountPage } from 'src/pages/CreateAccountPage/AsyncCreateAccountPage';
import { AsyncRegisterPage } from 'src/pages/RegisterPage/AsyncRegisterPage';
import { AsyncDetailPage } from 'src/pages/DetailPage/AsyncDetailPage';
import { AsyncFAQPage } from 'src/pages/FAQPage/AsyncFAQPage';
import { AsyncFAQSinglePage } from 'src/pages/FAQPage/FAQSinglePage/AsyncFAQSinglePage';
import { GhostPages } from 'src/pages/GhostPages';
import { AsyncGhostPostSinglePage } from 'src/pages/GhostPostSinglePage/AsyncGhostPostSinglePage';
import { AsyncLocationBedfordPage } from 'src/pages/LocationsPage/LocationBedfordPage/AsyncLocationBedfordPage';
import MaintenancePage from 'src/pages/MaintenancePage/MaintenancePage';
import { AsyncNewsAndEventsPage } from 'src/pages/NewsAndEventsPage/AsyncNewsAndEventsPage';
import { Page404 } from 'src/pages/Page404';
import './global.css';
import {
  LinkContext as BespokeLinkContext,
  ModalScrollContextProvider as BespokeScrollContextProvider,
} from '@up-riac/bespoke-ui';
import { GlobalDataContextProvider } from './lib/GlobalDataContext';
import { AsyncConsignmentPastSuccessPage } from './pages/ConsignmentPastSuccessPage/AsyncConsignmentPastSuccessPage';
import {
  AsyncConsignmentPastThatDrivesUsPage,
} from './pages/ConsignmentPastThatDrivesUsPage/AsyncConsignmentPastThatDrivesUsPage';
import { AsyncConsignmentProcessPage } from './pages/ConsignmentProcessPage/AsyncConsignmentProcessPage';
import { AsyncIconGalleryPage } from './pages/dev/AsyncIconGalleryPage';
import { AsyncToppleTriggers } from 'src/pages/dev/AsyncToppleTriggers';
import { AsyncLiveFallbackPage } from './pages/LiveFallbackPage/AsyncLiveFallbackPage';
import { PopupController } from 'src/components/PopupController';
import { HeaderContextProvider } from 'src/components/Header/components/HeaderContext';
import { RIACRedirect } from 'src/RIACRedirect';

const AsyncAccountPage = ({ ...props }) => <PageLoader
  id='src/pages/AccountPage/AccountPage'
  componentImport={async () => (await import('src/pages/AccountPage/AccountPage'))}
  {...props}/>;
const AsyncOrderCatalogPage = ({ ...props }) => <PageLoader
  id='src/pages/OrderCatalogPage/OrderCatalogPage'
  componentImport={() => import('src/pages/OrderCatalogPage/OrderCatalogPage') as any}
  {...props}/>;

const AsyncFrontPage = ({ ...props }) => <PageLoader
  id='src/pages/FrontPage/FrontPage'
  componentImport={() => import('src/pages/FrontPage/FrontPage')}
  {...props}/>;
const AsyncTermsAndConditionsPage = ({ ...props }) => <PageLoader
  id='src/pages/TermsAndConditionsPage'
  componentImport={() => import('src/pages/TermsAndConditionsPage')}
  {...props}/>;
const AsyncLoginPage = ({ ...props }) => <PageLoader
  id='src/pages/LoginPage'
  componentImport={() => import('src/pages/LoginPage')}
  {...props}/>;
const AsyncPasswordResetPage = ({ ...props }) => <PageLoader
  id='src/pages/AccountPage/PasswordReset/PasswordReset'
  componentImport={() => import('src/pages/AccountPage/PasswordReset/PasswordReset')}
  {...props}/>;
const AsyncPasswordResetPageWithCode = ({ ...props }) => <PageLoader
  id='src/pages/AccountPage/PasswordReset/PasswordResetWithCode'
  componentImport={() => import('src/pages/AccountPage/PasswordReset/PasswordResetWithCode')}
  {...props}/>;
const AsyncWishlistImport = ({ ...props }) => <PageLoader
  id='src/pages/AccountPage/WishlistImport/WishlistImport'
  componentImport={() => import('src/pages/AccountPage/WishlistImport/WishlistImport')}
  {...props}/>;
const AsyncAccountInfoValidatePage = ({ ...props }) => <PageLoader
  id='src/pages/AccountPage/AccountInfoValidatePage'
  componentImport={() => import('src/pages/AccountPage/AccountInfoValidatePage')}
  {...props}/>;
const AsyncEmailLinkVerifyPage = ({ ...props }) => <PageLoader
  id='src/pages/AccountPage/EmailLinkVerifyPage'
  componentImport={() => import('src/pages/EmailLinkVerifyPage/EmailLinkVerifyPage')}
  {...props}/>;

const Providers = ({ children, hashMatch }: React.PropsWithChildren<{ hashMatch: boolean }>): JSX.Element => (
  <OverlayProvider>  {/* React-Aria */}
    <LinkContext.Provider value={{ domainName: window.DOMAIN_NAME, hashMatch: hashMatch }}>
      <BespokeLinkContext.Provider value={{
        domainName: window.DOMAIN_NAME,
        hashMatch: hashMatch,
        buttonComponent: Link,
        buttonComponentLinkProp: 'to',
      }}>
        <ThemeContextProvider>
          <LoginContextProvider>
            <GlobalDataContextProvider>
              <HeaderContextProvider>
                <AccountHeaderContextProvider>
                  <UserItemContextProvider>
                    <ModalScrollContextProvider>
                      <ScrollContextProvider>
                        <BespokeScrollContextProvider>
                          <IntersectionObserverContextProvider>
                            <LazyLoadContextProvider>
                              <GlobalBannerContextProvider>
                                <AnalyticsContextProvider>
                                  {children}
                                </AnalyticsContextProvider>
                              </GlobalBannerContextProvider>
                            </LazyLoadContextProvider>
                          </IntersectionObserverContextProvider>
                        </BespokeScrollContextProvider>
                      </ScrollContextProvider>
                    </ModalScrollContextProvider>
                  </UserItemContextProvider>
                </AccountHeaderContextProvider>
              </HeaderContextProvider>
            </GlobalDataContextProvider>
          </LoginContextProvider>
        </ThemeContextProvider>
      </BespokeLinkContext.Provider>
    </LinkContext.Provider>
  </OverlayProvider>
);

export const MaintenanceModeRoutes = (): JSX.Element => {
  return <Switch>
    <Route path='/letmein' component={() => {
      Cookies.set('skipMaintenance', true as any); // This should probably be a string but i don't want to touch it and it works.
      if(window && window.location && window.location.pathname) window.location.pathname = '/';
      return (
        <div>Letting you in...</div>
      );
    }}/>
    <Route path='/' render={() => {
      return <MaintenancePage/>;
    }}/>
  </Switch>;
};

export const AppRoutes = (): JSX.Element => {
  return <Route path='/:p0?/:p1?/:p2?/:p3?/:p4?/:p5?' render={({ match }) => (
    <LoadableErrorBoundary onError={(error, info) => console.error(error, info)}>
      <Switch>
        <Route path='/qr/cs/' render={({ location }) => (
          <RIACRedirect to={'/detail' + location.pathname.slice(6)}/>
        )}/>

        {/* Auction URL redirects */}
        <Route exact path='/auctions' render={() => <RIACRedirect to='/gun-auctions'/>}/>
        <Route exact path='/auctions/' render={() => <RIACRedirect to='/gun-auctions'/>}/>
        <Route exact path='/pricesrealized' render={() => <RIACRedirect to='/gun-auctions'/>}/>
        <Route path='/auction/:rest+' render={({ match }) => (
          <RIACRedirect to={`/gun-auctions/${match.params.rest}`}/>
        )}/>
        <Route path='/auctions/:rest+' render={({ match }) => (
          <RIACRedirect to={`/gun-auctions/${match.params.rest}`}/>
        )}/>

        {/* Legacy item URL redirects */}
        <Route path='/viewitem/aid/:aid/lid/:lid' render={({ match }) => (
          <RIACRedirect to={`/detail/${match.params.aid}/${match.params.lid}`}/>
        )}/>
        <Route path='/viewitem/aid/:aid' render={({ match }) => (
          <RIACRedirect to={`/catalog/${match.params.aid}`}/>
        )}/>

        {/* Dead page redirects */}
        <Route path='/european_headgear' render={() => <RIACRedirect to='/catalog'/>}/>
        <Route path='/auction_photo.aspx' render={() => <RIACRedirect to='/catalog'/>}/>
        <Route path='/gunphotos' render={() => <RIACRedirect to='/catalog'/>}/>
        <Route path='/search' render={() => <RIACRedirect to='/catalog'/>}/>
        <Route exact path='/category' render={() => <RIACRedirect to='/catalog'/>}/>
        <Route exact path='/index.aspx' render={() => <RIACRedirect to='/'/>}/>

        {/* Typo/misspelling redirects */}
        <Route
          exact
          path='/(catalog|category)/deringer'
          render={() => <RIACRedirect to='/category/derringer'/>}
        />

        {/* Blog / news redirects */}
        <Route path='/blog' render={({ location }) => (
          <RIACRedirect to={'/riac-blog' + location.pathname.slice(5)}/>
        )}/>
        <Route exact path='/riac-blog' render={() => <RIACRedirect to='/news-and-events'/>}/>
        <Route exact path='/news' render={() => <RIACRedirect to='/news-and-events'/>}/>

        {/* Dead consignment redirects */}
        <Route exact path='/past-auction-successes'
          render={() => <RIACRedirect to='/consignment/past-auction-successes'/>}
        />
        <Route exact path='/consignment/who-we-are'
          render={() => <RIACRedirect to='/consignment'/>}
        />
        <Route exact path='/consignment/how-much-is-your-gun-worth/:guntype?/:mfg?/:model?'
          render={() => <RIACRedirect to='/consignment'/>/* <AsyncGunPriceEstimatorPage {...match}/>*/}
        />
        <Route path='/dev/icon-gallery' render={() => <AsyncIconGalleryPage/>}/>
        {/* <Route exact path='/whiskey-and-war-stories' render={() => <AsyncWhiskeyAndWarStoriesPage/>}/> */}
        <Route exact path='/about-us'
          render={() => <AsyncAboutUsPage/>}
        />
        <Route exact path='/gun-auctions'
          render={() => <AsyncAuctionsPage/>}
        />
        <Route exact path='/faq'
          render={() => <AsyncFAQPage/>}
        />
        <Route exact path='/live'
          render={() => <AsyncLiveFallbackPage/>}
        />
        <Route exact path='/login' render={() => <AsyncLoginPage/>}/>
        <Route exact path='/login/password-reset' render={() => <AsyncPasswordResetPage/>}/>
        <Route exact path='/login/password-reset/:code' render={() => <AsyncPasswordResetPageWithCode/>}/>
        <Route exact path='/email/linkVerify/:code' render={() => <AsyncEmailLinkVerifyPage/>}/>
        {ENABLE_NEW_REGISTRATION
          ? <Route path='/register' render={() => <AsyncRegisterPage/>}/>
          : null
        }
        <Route exact path='/create-account/:code?' render={() =>
          ENABLE_NEW_REGISTRATION ? <RIACRedirect to='/register'/> : <AsyncCreateAccountPage/>
        }/>
        <Route exact path='/consignment/past-auction-successes' render={() => <AsyncConsignmentPastSuccessPage/>}/>
        <Route exact path='/consignment/notable-collectors' render={() => <AsyncConsignmentNotableCollectorsPage/>}/>
        <Route exact path='/consignment/consignment-process'
          render={() => <AsyncConsignmentProcessPage/>}
        />
        <Route exact path='/consignment/the-past-that-drives-us'
          render={() => <AsyncConsignmentPastThatDrivesUsPage/>}
        />
        <Route exact path={'/topple_test_page'} render={() => <AsyncToppleTriggers />} />
        <Route exact path='/'
          render={() => <AsyncFrontPage/>}
        />
        <Route path='/(catalog|detail|category)'>
          <WishlistActionContextProvider>
            <CatalogHistoryContextProvider>
              <Switch>
                <Route path='/(catalog|category)'>
                  <Switch>
                    {landingPages.map((landingPage) => {
                      return (
                        <Route
                          key={landingPage.slug}
                          exact
                          path={`/(catalog|category)/${landingPage.slug}`}
                          render={() => <AsyncCatalogLandingPage basicLandingPage={landingPage}/>}
                        />
                      );
                    })}
                    <Route>
                      <Switch>
                        <Route path='/catalog/:p0?/:p1?/:p2?/:p3?/:p4?/:p5?'>
                          <AsyncCatalogPage/>
                        </Route>
                        <Page404/>
                      </Switch>
                    </Route>
                  </Switch>
                </Route>
                <Route exact path='/detail/:auctionid/:lotnumber'>
                  <AsyncDetailPage/>
                </Route>
                <Route exact path='/detail/:auctionid/:lotnumber/:slug'>
                  <AsyncDetailPage/>
                </Route>
                <Page404/>
              </Switch>
            </CatalogHistoryContextProvider>
          </WishlistActionContextProvider>
        </Route>
        <Route path='/account/validate/:type?/:success?/:code?'
          render={({ match }) => <AsyncAccountInfoValidatePage match={match}/>}
        />
        <Route path={'/account/wishlist/shared/:wlid?'}
          render={(match) => (
            <WishlistActionContextProvider>
              <AsyncWishlistImport {...match}/>
            </WishlistActionContextProvider>
          )}
        />
        <Route path='/account'
          render={() => <AsyncAccountPage/>}
        />
        <Route path='/news-and-events/:page?'
          render={() => <AsyncNewsAndEventsPage/>}
        />
        <GhostPages match={match}>
          <Switch>
            <Route path='/order-catalog'
              component={({ match }) => <AsyncOrderCatalogPage match={match}/>}
            />
            <Route exact path='/faq/:slug'
              render={(match) => <AsyncFAQSinglePage {...match}/>}
            />
            <Route exact path='/riac-blog/:title'
              render={(match) => <AsyncGhostPostSinglePage {...match}/>}
            />
            <Route exact path='/news/:title'
              render={(match) => <AsyncGhostPostSinglePage {...match}/>}
            />
            <Route exact path='/video/:title'
              render={(match) => <AsyncGhostPostSinglePage {...match}/>}
            />
            <Route exact path='/events/:title'
              render={(match) => <AsyncGhostPostSinglePage {...match}/>}
            />
            <Route exact path='/adcampaign'
              render={() => <AsyncAdCampaignsPage/>}
            />
            <Route exact path='/adcampaign/:slug'
              render={() => <AsyncAdCampaignsSinglePage/>}
            />
            <Route exact path='/consignment/getting-started'
              render={(match) => <AsyncConsignmentGettingStartedPage {...match}/>}
            />
            <Route exact path='/terms-and-conditions'
              render={() => <AsyncTermsAndConditionsPage/>}
            />
            <Route path='/location/bedford'
              render={() => <AsyncLocationBedfordPage/>}
            />
            <Route path='/gun-auctions/:slug'
              render={(match) => <AsyncAuctionsSinglePage {...match}/>}
            />
            <Route exact path='/consignment'
              render={() => <AsyncConsignmentPage/>}
            />
            <Route exact path='/about-us'
              render={() => <AsyncAboutUsPage/>}
            />
            <Route exact path='/contact' render={() => <AsyncContactPage/>}/>
            <Route exact path='/consignment/notable-collectors/:slug' render={() => <AsyncConsignmentSingleCollectorPage/>}/>
            <Route render={() => <Page404/>}/>
          </Switch>
        </GhostPages>
      </Switch>
    </LoadableErrorBoundary>
  )}/>;
};

export const App = (): JSX.Element => {
  const [hashMatch, setHashMatch] = useState<boolean>(true);
  const { location, ssr, skipMaintenance } = useContext(SsrInitDataContext);
  const Router: any = (ssr && StaticRouter) || BrowserRouter; // TODO: Bad any but they don't provide a better type
  const { socketHookManager } = useContext(SocketContext);

  useSocketEvent(socketHookManager, 'hashcheck', (msg) => {
    console.log('socket hashcheck', { current: VERSION_HASH, latest: msg.hash, match: VERSION_HASH === msg.hash });
    setHashMatch(VERSION_HASH === msg.hash);
  });

  if(MAINTENANCE_MODE == 'true' && (!skipMaintenance && !Cookies.get('skipMaintenance'))) {
    return <>
      <Providers hashMatch = { hashMatch }>
        <Router
          context={{}}
          location={location && location.href}
        >
          <ErrorBoundary>
            <div className='flex min-h-[101vh] flex-col print:min-h-0'>
              <ScrollToTop/>
              <MaintenanceModeRoutes/>
            </div>
          </ErrorBoundary>
        </Router>
      </Providers>
    </>;
  }

  return (<>
    <Providers hashMatch = { hashMatch }>
      <Router
        context={{}}
        location={location && location.href}
      >
        <div className='flex min-h-[101vh] flex-col print:min-h-0'>
          <ServerSideRenderingError/>
          <div>
            <ErrorBoundary>
              <Suspense>
                <YouHaveBeenLoggedOut/>
                <IncompleteRegistrationBanner/>
                <ScrollToTop/>
                <PopupController/>
                <Header/>
                <Suspense fallback={<Spinner/>}>
                  <div>
                    <AppRoutes/>
                  </div>
                  <div className='flex grow-[10000]'/>
                </Suspense>
                <Footer/>
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>
      </Router>
      <CrispChat/>
    </Providers>
  </>
  );
};
